import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X, Package } from "lucide-react";
import { useState, useEffect } from "react";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { href: "/#probleme", label: "Avant", isAnchor: true },
    { href: "/#solution", label: "Solution", isAnchor: true },
    { href: "/#fonctionnement", label: "Étapes", isAnchor: true },
    { href: "/#transporteurs", label: "Transporteurs vérifiés", isAnchor: true },
    { href: "/blog", label: "Blog", isRoute: true },
  ];

  const handleAnchorClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    const hash = href.split('#')[1];
    
    if (location.pathname === '/') {
      // Already on homepage, just scroll
      const element = document.getElementById(hash);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      // Navigate to homepage then scroll
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? "bg-background/80 backdrop-blur-xl border-b border-border shadow-sm" : "bg-transparent border-b border-transparent"}`}>
      <div className="container mx-auto px-4">
        <div className={`flex items-center justify-between transition-all duration-300 ${isScrolled ? 'h-16' : 'h-20'}`}>
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group">
            <div className={`rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center transition-all duration-300 ${isScrolled ? 'w-9 h-9' : 'w-10 h-10'}`}>
              <Package className={`text-primary-foreground transition-all duration-300 ${isScrolled ? 'w-4 h-4' : 'w-5 h-5'}`} />
            </div>
            <span className={`font-heading font-bold text-primary transition-all duration-300 ${isScrolled ? 'text-xl' : 'text-xl md:text-2xl'}`}>
              GoBled<span className="text-[#18304e]">Pro</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-6 ml-4">
            {navLinks.map(link => (
              link.isRoute ? (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`relative text-lg font-semibold tracking-wide transition-colors after:absolute after:left-0 after:bottom-0 after:h-0.5 after:w-0 after:bg-secondary after:transition-all hover:after:w-full ${isScrolled ? 'text-muted-foreground hover:text-primary' : 'text-foreground/80 hover:text-foreground'}`}
                >
                  {link.label}
                </Link>
              ) : link.isAnchor ? (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => handleAnchorClick(e, link.href)}
                  className={`relative text-lg font-semibold tracking-wide transition-colors after:absolute after:left-0 after:bottom-0 after:h-0.5 after:w-0 after:bg-secondary after:transition-all hover:after:w-full cursor-pointer ${isScrolled ? 'text-muted-foreground hover:text-primary' : 'text-foreground/80 hover:text-foreground'}`}
                >
                  {link.label}
                </a>
              ) : (
                <a 
                  key={link.href} 
                  href={link.href} 
                  className={`relative text-lg font-semibold tracking-wide transition-colors after:absolute after:left-0 after:bottom-0 after:h-0.5 after:w-0 after:bg-secondary after:transition-all hover:after:w-full ${isScrolled ? 'text-muted-foreground hover:text-primary' : 'text-foreground/80 hover:text-foreground'}`}
                >
                  {link.label}
                </a>
              )
            ))}
          </nav>

          {/* Desktop CTAs */}
          <div className="hidden md:flex items-center gap-3">
            <Button
              asChild
              size={isScrolled ? "sm" : "default"}
              className="font-medium transition-all duration-300 bg-primary hover:bg-primary/90 text-primary-foreground shadow-md hover:scale-[1.02] hover:shadow-lg active:scale-[0.98]"
            >
              <Link to="/demande-transport">
                <Package className="w-4 h-4 mr-2" />
                Envoyer un colis
              </Link>
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button className={`md:hidden p-2 rounded-lg transition-colors ${isScrolled ? 'hover:bg-muted' : 'hover:bg-foreground/10'}`} onClick={() => setIsMenuOpen(!isMenuOpen)} aria-label="Menu">
            {isMenuOpen ? <X className={`w-6 h-6 ${isScrolled ? 'text-foreground' : 'text-foreground'}`} /> : <Menu className={`w-6 h-6 ${isScrolled ? 'text-foreground' : 'text-foreground'}`} />}
          </button>
        </div>

        {/* Mobile Menu */}
        <div className={`md:hidden overflow-hidden transition-all duration-300 ${isMenuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
          <div className="py-4 border-t border-border/50 bg-background/95 backdrop-blur-xl rounded-b-2xl">
            <nav className="flex flex-col gap-1">
              {navLinks.map(link => (
                link.isRoute ? (
                  <Link
                    key={link.href}
                    to={link.href}
                    className="px-4 py-3 text-[15px] text-muted-foreground hover:text-primary hover:bg-muted rounded-lg transition-colors font-semibold tracking-wide"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {link.label}
                  </Link>
                ) : link.isAnchor ? (
                  <a
                    key={link.href}
                    href={link.href}
                    onClick={(e) => {
                      handleAnchorClick(e, link.href);
                      setIsMenuOpen(false);
                    }}
                    className="px-4 py-3 text-[15px] text-muted-foreground hover:text-primary hover:bg-muted rounded-lg transition-colors font-semibold tracking-wide cursor-pointer"
                  >
                    {link.label}
                  </a>
                ) : (
                  <a 
                    key={link.href} 
                    href={link.href} 
                    className="px-4 py-3 text-[15px] text-muted-foreground hover:text-primary hover:bg-muted rounded-lg transition-colors font-semibold tracking-wide" 
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {link.label}
                  </a>
                )
              ))}
              <div className="flex flex-col gap-2 mt-3 px-4">
                <Button asChild className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-medium">
                  <Link to="/demande-transport" onClick={() => setIsMenuOpen(false)}>
                    <Package className="w-4 h-4 mr-2" />
                    Demander un transport
                  </Link>
                </Button>
              </div>
            </nav>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;